import React, {
  useContext,
  useEffect,
  useState,
  useRef
} from 'react';
import { node, func } from 'prop-types';
import {
  Col, Row, Accordion, AccordionBody, AccordionTitle, Placeholder
} from '@thd-olt-component-react/core-ui';
import {
  extend, params, string, useDataModel, QueryContext,
  QueryProvider, shape, bool, arrayOf
} from '@thd-nucleus/data-sources';
import { useLifeCycleEventBus } from '@thd-olt-functional/utils';
import { ExperienceContext, useStoreId, useConfigService } from '@thd-nucleus/experience-context';
import { useHistory, useLocation, useParams } from '@thd-olt-component-react/router';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { Buybox } from '@thd-olt-component-react/buybox';
import { Discontinued } from '@thd-olt-component-react/discontinued';
import { AisleBay } from '@thd-olt-component-react/aisle-bay';
import { MediaGalleryDesktop } from '@thd-olt-component-react/media-gallery';
import { ProductInfoBar } from '@thd-olt-component-react/product-info-bar';
import { ConsumerCardMessaging } from '@thd-olt-component-react/consumer-card-messaging';
import { ProductOverviewAccordionTitle } from '@thd-olt-component-react/product-overview';
import { SpecificationsAccordionTitle } from '@thd-olt-component-react/specifications';
import { QuestionsAndAnswersAccordionTitle } from '@thd-olt-component-react/questions-and-answers';
import { RatingsAndReviewsAccordionTitle } from '@thd-olt-component-react/ratings-and-reviews';
import { FrequentlyBoughtTogether } from '@thd-olt-component-react/frequently-bought-together';
import { ThdRecsFbt } from '@thd-olt-component-react/thd-recs-fbt';
import { DynamicRecsWrapper,
  RecentlyViewedWrapper,
  SupplementalRecommendations } from '@thd-olt-component-react/thd-recs-containers';
import { SalientListItem, SalientPoints } from '@thd-olt-component-react/salient-points';
import { RelatedSearch } from '@thd-olt-component-react/related-search';
import { AugmentedReality, RoomVo } from '@thd-olt-component-react/augmented-reality';
import { Sharebelt, ShareBeltPrint, ShareBeltShareButton, ShareFavorite } from '@thd-olt-component-react/thd-sharebelt';
import { Price } from '@thd-olt-component-react/price';
import { DisclaimerMessage } from '@thd-olt-component-react/disclaimer-message';
import { Promotion } from '@thd-olt-component-react/promotion';
import { PromotionProducts } from '@thd-olt-component-react/promotion-products';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { ProductDetails } from '@thd-olt-component-react/product-details';
import { SponsoredPipBanner, SponsoredCarousel } from '@thd-olt-component-react/sponsored-content';
import { StickyNav } from '@thd-olt-component-react/sticky-nav';
import { SuperSku } from '@thd-olt-component-react/super-sku';
import { Metadata, ProductMetadata } from '@thd-olt-component-react/metadata';
import { OrderSample } from '@thd-olt-component-react/order-sample';
import { EmtLinks } from '@thd-olt-component-react/emt-links';
import { CalcLink } from '@thd-olt-component-react/moving-calculator';
import { PIPRedirector, DynamicComponent, AppContext } from '@thd-nucleus/app-render';
import { GlobalCustomConfigurator } from '@thd-olt-component-react/global-custom-configurator';
import { Hydrator, declareContexts } from '@thd-olt-component-react/hydrator';
import { PackagesMiniVisual } from '@thd-olt-component-react/packages-mini';
import { ProductConfigurator } from '@thd-olt-component-react/product-configurator';
import { EducationalSummary } from '@thd-olt-component-react/educational-summary';
import { ItemRelatedGroupsDesktopImporter } from '@thd-olt-component-react/item-related-groups';
import { FeatureBasedRecommendationsImporter } from '@thd-olt-component-react/feature-based-recommendations';
import { ProjectShoppingWrapper } from '@thd-olt-component-react/tools-materials';
import { Link } from '@one-thd/sui-atomic-components';
import {
  shouldDisplayPipInStock,
  shouldDisplayReferrerRecommendations,
  shouldDisplayVisuallySimilar,
  shouldDisplayPipAlternativeRecs,
  shouldDisplayPipCollections,
  shouldDisplaySupplementalRecs,
  shouldDisplayRecsFBT,
  shouldDisplayFbtbundle,
  shouldDisplayConfigurator,
  getAnchorSku,
  validatePipFlip,
  flipPip
} from './product-utils';
import { HammerWrenchIcon } from '../pipflip';
import {
  QuestionsAndAnswersAccordionBody, QuestionsAndAnswersAccordionBodyLoad, QuestionsAndAnswersAccordionBodyModel,
  RatingsAndReviewsAccordionBody, RatingsAndReviewsAccordionBodyLoad, RatingsAndReviewsAccordionBodyModel,
  SpecificationsAccordionBody, SpecificationsAccordionBodyLoad, SpecificationsAccordionBodyModel,
  ProductOverviewAccordionBody, ProductOverviewAccordionBodyLoad, ProductOverviewAccordionBodyModel
} from './dynamic-imports';
import ZoneCard from './zone-card';
import { HYDRATION_STANDARD_SCROLL_BUFFER, RECS_REQUEST_KEY, STICKY_HEADER_HEIGHT } from '../../constants';
import '../../styles/product-page.scss';

const ChildMap = ({ children }) => {
  return <Row><Col nopadding>{children}</Col></Row>;
};

ChildMap.propTypes = {
  children: node.isRequired,
};

export const Product = (props) => {
  declareContexts([QueryContext, ExperienceContext]);
  const { cookieUtils } = props;
  const [hasPipFlip, setHasPipFlip] = useState(false);
  const {
    configuredProductLabel,
  } = useLifeCycleEventBus('configurator.configurator_choice_selected_event');
  const productId = useParams()?.itemId;
  const [itemId, setItemId] = useState(productId);
  const ctx = useContext(ExperienceContext);
  const qctx = useContext(QueryContext);
  const { instance } = useContext(AppContext);
  const thdSeoBotDetected = instance?.thdSEOBotDetection;
  const { channel, segment = '', isDebugMode, isServer } = ctx;
  const storeId = useStoreId();
  const history = useHistory();
  const { pathname } = useLocation();
  const [mounted, setMounted] = useState(false);
  const fsRecsPipCollection = useConfigService('fs:isRecsCollectionEnable');
  const fsSupplementalRecs = useConfigService('fs:isSupplementalRecsEnable');
  const fsRecsFBT = useConfigService('fs:isFbtRecsEnable');

  const stickyNavRef = useRef(null);
  const productOverviewRef = useRef(null);
  const specificationsRef = useRef(null);
  const questionsRef = useRef(null);
  const ratingsRef = useRef(null);

  const arRef = useRef(null);
  const handleARClick = () => {
    arRef.current.click();
  };

  const updateStickyHeader = () => {
    const el = document.querySelector('.Header3');
    if (!el) return;
    el.classList.remove('Header3-sticky-desktop');
  };

  useEffect(() => {
    updateStickyHeader();
    setMounted(true);
  }, []);

  const onChange = (data = {}) => {
    const { canonicalUrl } = data;
    if (canonicalUrl) {
      history.push(canonicalUrl, { disableScroll: true });
    }
  };

  const { data: productData } = useDataModel('clientOnlyProduct', {
    variables: {
      itemId,
      storeId
    },
    skip: !itemId,
    ssr: false
  });

  const anchorSku = getAnchorSku(productData, itemId);
  const pipFlipValidation = validatePipFlip(productData, hasPipFlip);
  const hasPipFlipCookie = (typeof document !== 'undefined' && cookieUtils)
    ? cookieUtils.fed_ReadCookie('tnt_pipflip')
    : null;
  const triggerPipFlip = () => flipPip(
    configuredProductLabel,
    productData,
    hasPipFlip,
    productId,
    setHasPipFlip,
    itemId,
    anchorSku,
    setItemId
  );

  useEffect(() => {
    if (productId !== itemId && !hasPipFlip) {
      setItemId(productId);
    }
  }, [productId]);

  const isPackage = !!productData?.product?.info?.recommendationFlags?.packages;

  const hideRatingsIfHDPPSku = true;
  const isHDPPSku = productData?.product?.identifiers?.skuClassification === 'hdpp';

  const [showPackagesMini, setShowPackagesMini] = useState(isPackage);

  useEffect(() => {
    setShowPackagesMini(isPackage);
  }, [isPackage]);

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.trigger('configurator.configurator_omsId_event', itemId);
  }, [productData]);

  const [expanded, setExpanded] = useState('');

  const handleAccordionChange = (accordion, canCloseAccordion = true) => {
    if (!expanded[accordion] || canCloseAccordion) {
      setExpanded((prevState) => ({ ...prevState, [accordion]: !(prevState[accordion]) }));
    }
  };

  const scrollTo = (offset, callback) => {
    const fixedOffset = offset.toFixed();
    const onScroll = () => {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener('scroll', onScroll);
        callback();
      }
    };
    window.addEventListener('scroll', onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: 'smooth'
    });
  };

  const handleProductDetailsClick = (accordion) => {
    handleAccordionChange(accordion, false);
  };

  const handleViewMoreDetails = () => {
    const offset = productOverviewRef.current.offsetTop - STICKY_HEADER_HEIGHT;
    scrollTo(offset, () => handleAccordionChange('#product-section-overview', false));
  };

  const isPaint = productData?.product?.info?.dotComColorEligible;
  return (
    <>
      <ErrorBoundary name="redirector">
        <PIPRedirector disablePEPRedirect disableCanonicalRedirect disableCMMMCRedirect />
      </ErrorBoundary>
      <ErrorBoundary name="product-metadata">
        <Metadata>
          <ProductMetadata itemId={itemId} hideIfHDPPSku={hideRatingsIfHDPPSku} />
        </Metadata>
      </ErrorBoundary>
      <ErrorBoundary name="pip-content">
        <Row className="isBound">
          <Row>
            <ErrorBoundary name="sticknav">
              <StickyNav
                itemId={itemId}
                ref={stickyNavRef}
                ratingsRef={isHDPPSku ? null : ratingsRef}
                onRatingsClick={() => handleAccordionChange('#product-section-rr', false)}
                hideIfHDPPSku={hideRatingsIfHDPPSku}
              >
                <StickyNav.Links>
                  <StickyNav.Link targetRef={productOverviewRef}>
                    <div
                      className="navlink-pso"
                      onClick={() => handleAccordionChange('#product-section-overview', false)}
                      role="button"
                      tabIndex={0}
                    >Product Details
                    </div>
                  </StickyNav.Link>
                  <StickyNav.Link targetRef={specificationsRef}>
                    <div
                      className="navlink-specs"
                      onClick={() => handleAccordionChange('#product-section-key-feat', false)}
                      role="button"
                      tabIndex={0}
                    >Specifications
                    </div>
                  </StickyNav.Link>
                  <StickyNav.Link targetRef={questionsRef}>
                    <div
                      className="navlink-qa"
                      onClick={() => handleAccordionChange('#product-section-qa', false)}
                      role="button"
                      tabIndex={0}
                    >Questions & Answers
                    </div>
                  </StickyNav.Link>
                  {!isHDPPSku
                  && (
                    <StickyNav.Link targetRef={ratingsRef}>
                      <div
                        className="navlink-rr"
                        onClick={() => handleAccordionChange('#product-section-rr', false)}
                        role="button"
                        tabIndex={0}
                      >Customer Reviews
                      </div>
                    </StickyNav.Link>
                  )}
                </StickyNav.Links>
              </StickyNav>
            </ErrorBoundary>
          </Row>
          <Row className="u--paddingTop u--paddingBottom">
            <ErrorBoundary name="breadcrumb-productInfoBar-grp">
              <Col className="u__visible" sm="7" flatten>
                <ErrorBoundary name="breadcrumbs">
                  <Breadcrumbs
                    itemId={itemId}
                    useStencilDesign
                  />
                </ErrorBoundary>
              </Col>
              <Col sm="5" nopadding>
                <ErrorBoundary name="product-info-bar">
                  <div className="product-info-bar--hide-mobile u__flex-end">
                    <ProductInfoBar itemId={itemId} />
                  </div>
                </ErrorBoundary>
              </Col>
            </ErrorBoundary>
          </Row>
          {mounted && shouldDisplayReferrerRecommendations() && (
            <Row>
              <DynamicRecsWrapper
                schemaName="pipsem"
                anchorId={itemId}
                errorBoundary
                hideATC
                requestKey={RECS_REQUEST_KEY}
                hydrator={{
                  className: 'grid',
                  tag: ZoneCard,
                  delay: 0,
                  scrollBuffer: 0,
                  id: 'pipsem'
                }}
              />
            </Row>
          )}
          <Row>
            <ErrorBoundary name="zone-a">
              <Col nopadding={channel === 'desktop'}>
                <Row>
                  <Col>
                    <Discontinued itemId={itemId} />
                  </Col>
                  <Col sm="7" flatten className="product--sticky">
                    <ZoneCard className="zone-card overflow u__clearfix">
                      <Row>
                        <Col sm="12">
                          <ErrorBoundary name="product-details">
                            <ProductDetails itemId={itemId}>
                              <ProductDetails.Badge itemId={itemId} />
                              <ProductDetails.BrandCollection
                                showBrandHyperLink
                                hideRatingsIfHDPPSku={hideRatingsIfHDPPSku}
                              />
                              <div className="product-details__badge-title--wrapper">
                                <ProductDetails.Title />
                              </div>
                              <div className="product-details__favorites">
                                <Sharebelt itemId={itemId}>
                                  <ShareFavorite showCircle showFavoritesCount />
                                </Sharebelt>
                              </div>
                              <ProductDetails.Ratings
                                showBadge
                                target="#product-section-rr"
                                offsetselector="#sticky-nav"
                                qaselector="#product-section-qa"
                                onClick={(event) => handleProductDetailsClick(event)}
                                hideRatingsIfHDPPSku={hideRatingsIfHDPPSku}
                              />
                            </ProductDetails>
                          </ErrorBoundary>
                        </Col>
                      </Row>
                      <Col sm="12">
                        <ErrorBoundary name="media-gallery-desktop">
                          <MediaGalleryDesktop
                            itemId={itemId}
                            showLoader
                            onMediaClick={handleARClick}
                          />
                        </ErrorBoundary>
                      </Col>
                      <Col>
                        <ErrorBoundary name="sharebelt">
                          <Sharebelt horizontal itemId={itemId}>
                            <ShareBeltShareButton email />
                            <ShareBeltPrint />
                          </Sharebelt>
                        </ErrorBoundary>
                      </Col>
                    </ZoneCard>
                    <Col className="zone-card overflow">
                      {!isServer && productData?.product?.identifiers?.roomVOEnabled
                      && (
                        <ErrorBoundary name="RoomVo">
                          <RoomVo
                            itemId={itemId}
                          />
                        </ErrorBoundary>
                      )}
                    </Col>
                  </Col>
                  <Col sm="5" flatten>
                    <div className="overflow">
                      <Row>
                        <Col className="zone-card overflow">
                          <ErrorBoundary name="price">
                            <Price
                              channel="desktop"
                              displayEachUom={false}
                              itemId={itemId}
                              large
                              disableRangePricing
                              clsRemediation={{
                                placeholders: true,
                                preservePlaceholders: true
                              }}
                            />
                          </ErrorBoundary>
                        </Col>
                        <Col className="overflow zone-card promotion__card">
                          <ErrorBoundary name="promotion">
                            <Promotion channel="desktop" storeId={storeId} itemId={itemId} />
                          </ErrorBoundary>
                        </Col>
                        <Col className="zone-card overflow">
                          <ErrorBoundary name="disclaimer-message">
                            <DisclaimerMessage itemId={itemId} />
                          </ErrorBoundary>
                        </Col>

                        <Col className="zone-card overflow consumer-credit-messaging__card">
                          <ErrorBoundary name="consumer-card-messaging">
                            <ConsumerCardMessaging
                              itemId={itemId}
                              clsRemediation={{
                                placeholders: true,
                                preservePlaceholders: true
                              }}
                            />
                          </ErrorBoundary>
                        </Col>

                        <Col className="zone-card overflow">
                          <ErrorBoundary name="promotion-products">
                            <PromotionProducts itemId={itemId} />
                          </ErrorBoundary>
                        </Col>

                        <Col className="zone-card">
                          <ErrorBoundary name="salient">
                            <SalientPoints itemId={itemId} max={3}>
                              <SalientListItem>
                                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                                <Link
                                  underline="always"
                                  onClick={handleViewMoreDetails}
                                >
                                  View More Details
                                </Link>
                              </SalientListItem>
                            </SalientPoints>
                          </ErrorBoundary>
                        </Col>

                        <Col className="zone-card overflow">
                          <ErrorBoundary name="aisle-bay">
                            <AisleBay
                              itemId={itemId}
                              clsRemediation={{
                                placeholders: true,
                                preservePlaceholders: false
                              }}
                            />
                          </ErrorBoundary>
                        </Col>

                        { shouldDisplayConfigurator(productData, isServer)
                          && (
                            <Col className="zone-card overflow">
                              <ErrorBoundary name="product-configurator">
                                <ProductConfigurator
                                  pipFlip={triggerPipFlip}
                                  itemId={itemId}
                                />
                              </ErrorBoundary>
                            </Col>
                          )}
                        { !shouldDisplayConfigurator(productData, isServer)
                        && (
                          <Col className="zone-card overflow">
                            <ErrorBoundary name="super-sku">
                              <SuperSku
                                itemId={productId}
                                onChange={onChange}
                                large={isPaint}
                                centeredCards={isPaint}
                                disableMediaAndAvailability={isPaint}
                              />
                              { hasPipFlipCookie && pipFlipValidation
                              && (
                                <div>
                                  <div className="attribute__label">
                                    <div className="label">
                                      Customize:
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    className="super-sku__inline-tile border-radius--medium"
                                    onClick={triggerPipFlip}
                                    style={{ marginTop: 5, alignItems: 'center' }}
                                  >
                                    { HammerWrenchIcon }
                                    { hasPipFlipCookie === 'A' && pipFlipValidation && (
                                      <span style={{ paddingLeft: 10 }}>
                                        Create Your Own
                                      </span>
                                    )}
                                    { hasPipFlipCookie === 'B' && pipFlipValidation && (
                                      <span style={{ paddingLeft: 10 }}>
                                        See Additional Options
                                      </span>
                                    )}
                                  </button>
                                </div>
                              )}
                            </ErrorBoundary>
                          </Col>
                        )}
                        <Col className="zone-card">
                          <ErrorBoundary name="order-sample">
                            <OrderSample itemId={itemId}>
                              Need a closer look?
                            </OrderSample>
                          </ErrorBoundary>
                        </Col>
                        {productData?.product?.identifiers?.productType !== 'CONFIGURABLE_BLINDS' && !hasPipFlip
                          && (
                            <Col flatten>
                              <ErrorBoundary name="buybox">
                                <Buybox itemId={itemId} sticky showCards />
                              </ErrorBoundary>
                            </Col>
                          )}
                        <Col className="zone-card">
                          <ErrorBoundary name="moving-calculator">
                            <CalcLink itemId={itemId} />
                          </ErrorBoundary>
                        </Col>

                        <Col className="zone-card overflow">
                          <ErrorBoundary name="gcc">
                            <GlobalCustomConfigurator itemId={itemId} />
                          </ErrorBoundary>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                </Row>
              </Col>
            </ErrorBoundary>
          </Row>
          <Row>
            <ErrorBoundary name="augmented-reality">
              <ZoneCard>
                <AugmentedReality
                  itemId={itemId}
                  instructions
                  forwardedRef={arRef}
                />
              </ZoneCard>
            </ErrorBoundary>
          </Row>
          <ErrorBoundary name="collapsible-sections">
            <DynamicComponent pageType="pip">
              <Hydrator
                id="collapsible-sections-accordion"
                className="grid"
                placeholder={(
                  <>
                    <div
                      id="product-section-overview"
                      className="accordion-placeholder"
                      ref={productOverviewRef}
                    >
                      <Placeholder width="100%" height="82px" color="#F5F5F5" />
                    </div>
                    <div id="product-section-key-feat" className="accordion-placeholder">
                      <Placeholder width="100%" height="82px" color="#F5F5F5" />
                    </div>
                    <div id="product-section-qa" className="accordion-placeholder">
                      <Placeholder width="100%" height="82px" color="#F5F5F5" />
                    </div>
                    <div id="product-section-rr" className="accordion-placeholder">
                      <Placeholder width="100%" height="82px" color="#F5F5F5" />
                    </div>
                  </>
                )}
              >
                <ZoneCard className="zone-card">
                  <div
                    id="product-section-overview"
                    className="product-section-overview"
                    ref={productOverviewRef}
                  >
                    <Accordion
                      icon={{ size: 'sm' }}
                      iconPosition="right"
                      key="product-details-accordion"
                      className="collapsible-section"
                      expanded={expanded['#product-section-overview'] || thdSeoBotDetected}
                      onChange={() => handleAccordionChange('#product-section-overview')}
                    >
                      <AccordionTitle className="accordion-title-container">
                        <ProductOverviewAccordionTitle />
                      </AccordionTitle>
                      <AccordionBody>
                        <DynamicComponent pageType="pip">
                          <Hydrator
                            id="product-section-product-overview"
                            className="grid"
                            waitFor={ProductOverviewAccordionBodyLoad}
                            placeholder={(
                              <Placeholder
                                width="100%"
                                height="2000px"
                                color="#F5F5F5"
                              />
                            )}
                          >
                            <ProductOverviewAccordionBody
                              itemId={itemId}
                              showReturnMessage
                              revJet
                              layoutGroup={{
                                tag: Row,
                              }}
                              qctx={qctx}
                            />
                          </Hydrator>
                        </DynamicComponent>
                      </AccordionBody>
                    </Accordion>
                  </div>
                  <div
                    id="product-section-key-feat"
                    className="product-section-key-feat"
                    ref={specificationsRef}
                  >
                    <Accordion
                      icon={{ size: 'sm' }}
                      iconPosition="right"
                      key="specifications-accordion"
                      className="collapsible-section"
                      expanded={expanded['#product-section-key-feat'] || thdSeoBotDetected}
                      onChange={() => handleAccordionChange('#product-section-key-feat')}
                    >
                      <AccordionTitle className="accordion-title-container">
                        <SpecificationsAccordionTitle itemId={itemId} />
                      </AccordionTitle>
                      <AccordionBody>
                        <DynamicComponent pageType="pip">
                          <Hydrator
                            id="product-section-specifications"
                            className="grid"
                            waitFor={SpecificationsAccordionBodyLoad}
                            placeholder={(
                              <Placeholder
                                width="100%"
                                height="700px"
                                color="#F5F5F5"
                              />
                            )}
                          >
                            <SpecificationsAccordionBody
                              itemId={itemId}
                              layoutGroup={{
                                tag: Row,
                              }}
                              qctx={qctx}
                            />
                          </Hydrator>
                        </DynamicComponent>
                      </AccordionBody>
                    </Accordion>
                  </div>
                  <div
                    id="product-section-qa"
                    className="product-section-qa"
                    ref={questionsRef}
                  >
                    <Accordion
                      key="questions-answers-accordion"
                      icon={{ size: 'sm' }}
                      iconPosition="right"
                      className="collapsible-section"
                      expanded={expanded['#product-section-qa'] || thdSeoBotDetected}
                      onChange={() => handleAccordionChange('#product-section-qa')}
                    >
                      <AccordionTitle className="accordion-title-container">
                        <QuestionsAndAnswersAccordionTitle
                          itemId={itemId}
                          layoutGroup={{
                            tag: Row,
                          }}
                          hydrator={{
                            className: 'grid'
                          }}
                          dynamic={{
                            pageType: 'pip'
                          }}
                        />
                      </AccordionTitle>
                      <AccordionBody>
                        <DynamicComponent pageType="pip">
                          <Hydrator
                            id="product-section-questions-answers"
                            className="grid"
                            waitFor={QuestionsAndAnswersAccordionBodyLoad}
                            placeholder={(
                              <Placeholder
                                width="100%"
                                height="700px"
                                color="#F5F5F5"
                              />
                            )}
                          >
                            <QuestionsAndAnswersAccordionBody
                              itemId={itemId}
                              seoPageNumber={1}
                              layoutGroup={{
                                tag: Row,
                              }}
                              qctx={qctx}
                            />
                          </Hydrator>
                        </DynamicComponent>
                      </AccordionBody>
                    </Accordion>
                  </div>
                  {!isHDPPSku
                        && (
                          <div
                            id="product-section-rr"
                            className="product-section-rr"
                            ref={ratingsRef}
                          >
                            <Accordion
                              key="ratings-reviews-accordion"
                              icon={{ size: 'sm' }}
                              iconPosition="right"
                              className="collapsible-section collapsible-section--bottom-border"
                              expanded={expanded['#product-section-rr'] || thdSeoBotDetected}
                              onChange={() => handleAccordionChange('#product-section-rr')}
                            >
                              <AccordionTitle className="accordion-title-container">
                                <RatingsAndReviewsAccordionTitle
                                  itemId={itemId}
                                  layoutGroup={{
                                    tag: Row,
                                  }}
                                  hydrator={{
                                    className: 'grid'
                                  }}
                                  dynamic={{
                                    pageType: 'pip'
                                  }}
                                />
                              </AccordionTitle>
                              <AccordionBody>
                                <DynamicComponent pageType="pip">
                                  <Hydrator
                                    id="product-section-ratings-reviews"
                                    className="grid"
                                    waitFor={RatingsAndReviewsAccordionBodyLoad}
                                    placeholder={(
                                      <Placeholder
                                        width="100%"
                                        height="2000px"
                                        color="#F5F5F5"
                                      />
                                    )}
                                  >
                                    <RatingsAndReviewsAccordionBody
                                      itemId={itemId}
                                      qctx={qctx}
                                      prosAndCons
                                    />
                                  </Hydrator>
                                </DynamicComponent>
                              </AccordionBody>
                            </Accordion>
                          </div>
                        )}
                </ZoneCard>
              </Hydrator>
            </DynamicComponent>
          </ErrorBoundary>
          {shouldDisplayPipInStock(productData) && (
            <Row>
              <DynamicRecsWrapper
                schemaName="pipinstock"
                anchorId={itemId}
                errorBoundary
                requestKey={RECS_REQUEST_KEY}
                hideATC
                hydrator={{
                  className: 'grid',
                  tag: ZoneCard,
                  id: 'pipinstock',
                  scrollBuffer: 300
                }}
              />
            </Row>
          )}
          {showPackagesMini && (
            <PackagesMiniVisual
              itemId={itemId}
              layoutGroup={{
                tag: Row,
              }}
              dynamic={{
                pageType: 'pip'
              }}
              hydrator={{
                className: 'grid',
                tag: ZoneCard
              }}
              callOnLoaded={({ didRender }) => {
                setShowPackagesMini(didRender);
              }}
            />
          )}
          {!showPackagesMini && (
            <>
              {!shouldDisplayFbtbundle(productData) && shouldDisplayRecsFBT(productData) && fsRecsFBT ? (
                <Row>
                  <ThdRecsFbt
                    itemId={itemId}
                    errorBoundary
                    hydrator={{
                      className: 'grid',
                      tag: ZoneCard,
                      id: 'fbt',
                    }}
                  />
                </Row>
              ) : (
                <FrequentlyBoughtTogether
                  itemId={itemId}
                  layoutGroup={{
                    tag: Row,
                  }}
                  dynamic={{
                    pageType: 'pip'
                  }}
                  hydrator={{
                    className: 'grid',
                    tag: ZoneCard
                  }}
                />
              )}
            </>
          )}
          <SponsoredPipBanner
            pageContext={{
              label: 'pip',
              data: {
                itemId,
                treatment: 'A'
              },
            }}
            layoutGroup={{
              name: 'pip-sponsored-banner-A'
            }}
            dynamic={{
              pageType: 'pip'
            }}
            hydrator={{
              id: 'hydrated-sponsored-pip-banner-A'
            }}
          />
          <EducationalSummary
            itemId={itemId}
            qctx={qctx}
            layoutGroup={{
              tag: Row,
            }}
            dynamic={{
              pageType: 'pip'
            }}
            hydrator={{
              className: 'grid'
            }}
          />
          {fsRecsPipCollection && shouldDisplayPipCollections(productData) && (
            <Row>
              <DynamicRecsWrapper
                schemaName="pipcollection"
                anchorId={itemId}
                requestKey={RECS_REQUEST_KEY}
                isCategoryTab
                errorBoundary
                maxResults="20"
                hideFavorites={false}
                hydrator={{
                  className: 'grid',
                  tag: ZoneCard,
                  id: 'pipcollection',
                  delay: 1500,
                  scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
                }}
              />
            </Row>
          )}
          <Row>
            <ItemRelatedGroupsDesktopImporter
              accordion
              errorBoundary
              dsNext
              itemId={itemId}
              pipCollectionFlag={shouldDisplayPipCollections(productData)}
              supplementalRecsFS={fsSupplementalRecs}
              showMobileCardTabs
              maxItems={6}
              qctx={qctx}
              rowHeader="You Might Also Need"
              dynamic={{
                pageType: 'pip'
              }}
              hydrator={{
                className: 'grid',
                tag: ZoneCard
              }}
            />
          </Row>
          {fsSupplementalRecs && shouldDisplaySupplementalRecs(productData) && (
            <Row>
              <SupplementalRecommendations
                schemaName="irgaccessories"
                anchorId={itemId}
                title="You Might Also Need"
                buttonValue="View Details"
                requestKey={RECS_REQUEST_KEY}
                errorBoundary
                hydrator={{
                  className: 'grid',
                  tag: ZoneCard,
                  id: 'irgaccessories',
                  delay: 1500,
                  scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
                }}
              />
            </Row>
          )}
          <ErrorBoundary name="sponsored-carousel-nucleus">
            <SponsoredCarousel
              pageContext={{
                schema: 'pip_sponsored',
                data: { itemId },
                isPip: true,
              }}
              showSponsoredCarousel
              dynamic={{
                pageType: 'pip'
              }}
              hydrator={{
                className: 'grid',
                scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
              }}
            />
          </ErrorBoundary>
          <FeatureBasedRecommendationsImporter
            hideOnMobile
            itemId={itemId}
            qctx={qctx}
            layoutGroup={{
              tag: Row,
            }}
            hydrator={{
              tag: ZoneCard,
              className: 'grid'
            }}
            dynamic={{
              pageType: 'pip'
            }}
          />
          {shouldDisplayPipAlternativeRecs(productData) && (
            <Row>
              <DynamicRecsWrapper
                schemaName="pip_alternatives"
                anchorId={itemId}
                errorBoundary
                requestKey={RECS_REQUEST_KEY}
                hideATC
                hydrator={{
                  className: 'grid',
                  tag: ZoneCard,
                  id: 'pip_alternatives',
                  scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
                }}
              />
            </Row>
          )}
          {shouldDisplayVisuallySimilar(productData) && (
            <Row>
              <DynamicRecsWrapper
                schemaName="visuallysimilar_1_0"
                anchorImage={productData?.product?.media?.image?.url}
                anchorId={itemId}
                errorBoundary
                requestKey={RECS_REQUEST_KEY}
                hydrator={{
                  className: 'grid',
                  tag: ZoneCard,
                  id: 'visuallysimilar_1_0',
                  scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
                }}
              />
            </Row>
          )}
          <Row>
            <QueryProvider cacheKey="rv-pip-recs" persist>
              <RecentlyViewedWrapper
                schemaName="rv_gm_pip_rr"
                errorBoundary
                hydrator={{
                  className: 'grid',
                  tag: ZoneCard,
                  id: 'rv_gm_pip_rr',
                  delay: 2000,
                  scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
                }}
              />
            </QueryProvider>
          </Row>
          <RelatedSearch
            itemId={itemId}
            layoutGroup={{
              tag: ChildMap
            }}
            dynamic={{
              pageType: 'pip'
            }}
            hydrator
          />
          <EmtLinks
            path={pathname}
            layoutGroup={{
              tag: Row
            }}
            dynamic={{
              pageType: 'pip',
            }}
            hydrator={{
              className: 'grid'
            }}
          />
        </Row>
      </ErrorBoundary>
      <ProjectShoppingWrapper
        breadcrumbs={productData?.product?.taxonomy?.breadCrumbs}
        channel={channel}
        itemId={itemId}
        projectId={productData?.product?.projectDetails?.projectId}
      />
    </>
  );
};

Product.displayName = 'ProductPage';

Product.propTypes = {
  cookieUtils: func
};

Product.defaultProps = {
  cookieUtils: null
};

Product.dataModel = extend(
  {
    product: params({ itemId: string().isRequired() }).shape({
      fulfillment: params({ storeId: string(), zipCode: string() }).shape({
        backordered: bool()
      }),
      info: shape({
        dotComColorEligible: bool()
      }),
      identifiers: shape({
        skuClassification: string()
      }),
    })
  },
  {
    clientOnlyProduct: params({ itemId: string().isRequired }).shape({
      itemId: string(),
      dataSources: string(),
      info: shape({
        gccExperienceOmsId: string(),
        recommendationFlags: shape({
          visualNavigation: bool(),
          pipCollections: bool(),
          packages: bool(),
          ACC: bool(),
          frequentlyBoughtTogether: bool(),
          bundles: bool()
        }),
        dotComColorEligible: bool()
      }),
      identifiers: shape({
        roomVOEnabled: bool(),
        productType: string(),
        skuClassification: string()
      }),
      availabilityType: shape({
        discontinued: bool(),
        type: string()
      }),
      fulfillment: params({ storeId: string() }).shape({
        backordered: bool(),
        fulfillmentOptions: arrayOf(shape({
          type: string(),
          fulfillable: bool(),
          services: arrayOf(shape({
            type: string(),
            locations: arrayOf(shape({
              isAnchor: bool(),
              inventory: shape({
                isLimitedQuantity: bool(),
                isOutOfStock: bool()
              })
            }))
          }))
        })),
      }),
      projectDetails: shape({
        projectId: string()
      })
    })
  },
  {
    clientOnlyProduct: params({ itemId: string().isRequired }).shape({
      seoDescription: string()
    })
  },
  PIPRedirector,
  ProductMetadata,
  StickyNav,
  Breadcrumbs,
  Sharebelt,
  Discontinued,
  Promotion,
  ProductInfoBar,
  MediaGalleryDesktop,
  AisleBay,
  ProductDetails,
  SalientPoints,
  Price,
  ConsumerCardMessaging,
  DisclaimerMessage,
  GlobalCustomConfigurator,
  OrderSample,
  SuperSku,
  CalcLink,
  Buybox,
  AugmentedReality,
  FrequentlyBoughtTogether,
  ThdRecsFbt,
  ItemRelatedGroupsDesktopImporter,
  FeatureBasedRecommendationsImporter,
  ProductOverviewAccordionBodyModel,
  SpecificationsAccordionBodyModel,
  QuestionsAndAnswersAccordionBodyModel,
  RatingsAndReviewsAccordionBodyModel,
  EducationalSummary,
  EmtLinks,
  RelatedSearch,
  PromotionProducts,
  PackagesMiniVisual,
  DynamicRecsWrapper,
  RecentlyViewedWrapper
);
